/*
 * The admin site's words in English: the editor section (S48, extended to the
 * admin site 2026-10-29) - the markdown editor with its toolbar, dialogs and
 * notices, the Images and Attachments cards under it, and the preview's notices
 * about links.
 *
 * `noun` is the editor's own word for what is being written, 'article' or
 * 'release note', exactly as MarkdownEditor's prop and the image owner carry it.
 * The English splices it into the sentence as it always did; the Danish takes
 * it as a kind and chooses the whole sentence (editor.da.ts).
 */

import type { VideoProvider } from '@accotool-kb/shared';

/** The noun with a capital, for a label: 'Article', 'Release note'. */
function capitalised(noun: string): string {
  return noun.length === 0 ? noun : `${noun[0]!.toUpperCase()}${noun.slice(1)}`;
}

/** The noun with its indefinite article and a capital: 'An article', 'A release note'. */
function capitalisedIndefinite(noun: string): string {
  return `${/^[aeiou]/i.test(noun) ? 'An' : 'A'} ${noun}`;
}

/** 'once', '3 times'. */
function times(count: number): string {
  return count === 1 ? 'once' : `${count} times`;
}

const PROVIDERS: Record<VideoProvider, string> = {
  youtube: 'YouTube video',
  vimeo: 'Vimeo video',
  file: 'Video file',
};

export const editorEn = {
  /** The formatting toolbar and the view switch (MarkdownEditor.tsx). */
  toolbar: {
    label: 'Formatting',
    bold: 'Bold',
    italic: 'Italic',
    inlineCode: 'Inline code',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    bulletedList: 'Bulleted list',
    numberedList: 'Numbered list',
    quote: 'Quote',
    codeBlock: 'Code block',
    table: 'Table',
    externalLink: 'External link',
    /** "Another" only where the text is itself an article. */
    articleLink: (noun: string): string =>
      noun === 'article' ? 'Link to another knowledge base article' : 'Link to a knowledge base article',
    insertImage: 'Insert image',
    insertImageHint: 'Insert image. You can also paste or drop one into the text.',
    insertVideo: 'Insert video',
    insertVideoHint: 'Insert video: YouTube, Vimeo, or a direct link to a video file',
    view: 'View',
    write: 'Write',
    split: 'Split',
    preview: 'Preview',
  },

  /**
   * The words the toolbar puts INTO the text when nothing is selected, for the
   * author to type over - content once saved, so in the language the author is
   * working in (the owner, 2026-10-29). The markdown around them is the same in
   * both languages.
   */
  inserted: {
    boldText: 'bold text',
    italicText: 'italic text',
    code: 'code',
    linkText: 'link text',
    tableColumn: 'Column',
    tableValue: 'Value',
  },

  /** Around the textarea: the save-first notice, the textarea, an image refused before upload (MarkdownEditor.tsx). */
  editor: {
    saveFirstTitle: (noun: string) => `Save the ${noun} before adding an image.`,
    saveFirstBody: (noun: string) =>
      `An image is stored against the ${noun} it belongs to, and this ${noun} does not exist yet. ` +
      'Save it once, then paste or drop the image again.',
    replaceStored: 'Replace the stored file',
    bodyLabel: (noun: string) => `${capitalised(noun)} body, in markdown`,
    placeholder: (noun: string) =>
      `Write the ${noun} in markdown.\n\n` +
      (noun === 'article'
        ? 'Use the article-link button above to cross-reference another article; the link follows it '
        : 'Use the article-link button above to link to an article; the link follows it ') +
      'even if its address changes.\n\n' +
      'Paste or drop a screenshot straight into the text to attach it, then write the ' +
      'alt text where the caret is left.',
    /** The row's name for a file that came without one. */
    pastedImage: 'pasted image',
    fileEmpty: 'That file is empty.',
    /** `limit` is the rule, t.admin.rules.imageSizeLimit. */
    imageTooLarge: (size: string, limit: string) => `That image is ${size}. ${limit}`,
    fileUnreadable: 'That file could not be read.',
    notAnImage: 'That file is not a supported image.',
    uploadFailed: 'The image could not be uploaded.',
  },

  /** An upload row, for an image and an attachment alike (MarkdownEditor.tsx, AttachmentUploads.tsx). */
  uploads: {
    uploading: (fileName: string) => `Uploading ${fileName}`,
    percent: (percent: number) => `${percent}%`,
    failed: 'The file could not be uploaded.',
  },

  /** What is wrong with the images in the text (MarkdownEditor.tsx). */
  audit: {
    title: (noun: string) => `Check the images in this ${noun}`,
    /** {names} is the list of file names. */
    notAttached: 'Referenced but not attached: {names}. Paste or drop the file, or correct the name.',
    /** {requirement} is t.admin.rules.imageFileNameRequirement. */
    badName: 'Not a name an image can be stored under: {names}. {requirement}',
    notLoaded: 'These image destinations will not be loaded at all:',
    empty: '(empty)',
    /** Why a destination will never become a src, after its target and a dash. */
    unsafe: {
      empty: 'there is no file name between the brackets.',
      scheme: 'only http and https addresses are loaded; a data: or javascript: image is refused.',
      protocolRelative:
        'an address starting with // loads from another site over whichever scheme this page used. Write it in full, with https://.',
      path: 'an attached image is named by a bare file name, with no folders, query or fragment.',
      other: 'it is not a file name or a full https address.',
    },
    /** `named` puts {names} in, for the images that have a file name. */
    missingAlt: (count: number, named: boolean) =>
      `${count === 1 ? 'One image has' : `${count} images have`} no alt text${named ? ' ({names})' : ''}. ` +
      'Describe what the image shows, for readers using a screen reader and for everyone when it fails to load.',
  },

  /** "Insert a video" (VideoInsertDialog.tsx). */
  videoDialog: {
    title: 'Insert a video',
    cancel: 'Cancel',
    insert: 'Insert',
    linkLabel: 'Video link',
    prompt: 'Paste a YouTube or Vimeo link, or a direct link to a video file.',
    recognised: (provider: VideoProvider) => `${PROVIDERS[provider]} — will be embedded in the article.`,
    notEmbeddable:
      'That link cannot be embedded. Supported: YouTube, Vimeo, or a direct https link to an .mp4, .m4v, .webm, .ogv or .ogg file.',
  },

  /** "Link to another article" (ArticlePickerDialog.tsx). */
  articlePicker: {
    title: (noun: string): string => (noun === 'article' ? 'Link to another article' : 'Link to an article'),
    tokenHint: (noun: string) => `Inserts a token that is resolved when the ${noun} is read, not when it is written.`,
    cancel: 'Cancel',
    insertLink: 'Insert link',
    findLabel: 'Find an article',
    findPlaceholder: 'Title, slug or body text…',
    searching: 'Searching…',
    noMatch: 'No articles match.',
    untitled: '(untitled)',
    linkTextLabel: 'Link text (optional)',
    linkTextPlaceholder: "Leave empty to use the target article's title",
    linkTextHint:
      'With no link text the reader sees the target’s current title, so renaming the article updates every link to it.',
  },

  /** The Images card (ArticleImagesPanel.tsx). */
  images: {
    heading: 'Images',
    ceiling: (noun: string, max: number) => `${capitalisedIndefinite(noun)} can carry at most ${max} images.`,
    noneAttached: 'none attached',
    /** `size` is the total, already written by t.article.byteSize. */
    count: (count: number, max: number, size: string) => `${count} of ${max} · ${size}`,
    refresh: 'Refresh',
    refreshHint: 'Ask the server what is attached now',
    allUsed: 'Every attached image is referenced by the text.',
    deleteUnusedHint: 'Delete every image the text does not reference',
    /** 0 is the disabled button, with nothing to count. */
    deleteUnused: (count: number) => (count === 0 ? 'Delete unused' : `Delete ${count} unused`),
    emptyHint: (noun: string) =>
      `Paste or drop a screenshot into the text above to attach it. It is stored against this ${noun} ` +
      'and served from it, so there is nothing to upload anywhere else.',
    notUsed: 'not used',
    notUsedHint: 'Nothing in the text refers to it, so no reader will ever see it.',
    used: (count: number) => `used ${times(count)}`,
    insert: 'Insert',
    insertHint: 'Insert a reference to this image at the caret',
    delete: 'Delete',
    deleteTitle: (fileName: string) => `Delete ${fileName}`,
    nothingChangedYet: 'Nothing has been changed yet.',
    cancel: 'Cancel',
    deleting: 'Deleting…',
    deleteImage: 'Delete the image',
    stillShown: (count: number) => `The text you are editing still shows this image ${times(count)}.`,
    stillShownBody:
      'Those references will be left pointing at nothing. Readers get a labelled placeholder carrying the alt ' +
      'text where the picture was, so the page does not break - but nothing shows either. Remove the ' +
      'references from the text as well.',
    /** {size}, {type} (PNG, JPEG, ...), {by} (who uploaded it) and {date}. */
    details: '{size} · {type}, uploaded by {by} on {date}.',
    deleteBody: (noun: string) =>
      `An image belongs to the ${noun} rather than to a revision, so this removes it from every revision ` +
      'that showed it, including the one readers have now. The bytes are gone for good; the only way back ' +
      'is to upload the file again.',
    deleted: (fileName: string, danglingReferences: number) =>
      danglingReferences > 0
        ? `${fileName} was deleted. The published text still references it ${times(danglingReferences)}, ` +
          'so readers will see a placeholder until you remove the reference and save.'
        : `${fileName} was deleted.`,
    bulkTitle: (count: number) => (count === 1 ? 'Delete the unused image' : `Delete ${count} unused images`),
    bulkConfirm: (count: number) => (count === 1 ? 'Delete the image' : `Delete ${count} images`),
    savedStillShows: (count: number) =>
      `The saved text still shows ${count === 1 ? 'one of these images' : `${count} of these images`}.`,
    savedStillShowsBody:
      'You have removed the references in the editor but not saved yet. Until you save, readers get a ' +
      'labelled placeholder carrying the alt text where each picture was - the page does not break, but ' +
      'nothing shows either.',
    /** `size` is the total, already written by t.article.byteSize. */
    bulkIntro: (count: number, size: string) =>
      `Nothing in the text refers to ${count === 1 ? 'this image' : 'these images'} (${size} in total), ` +
      `so no reader can see ${count === 1 ? 'it' : 'them'}:`,
    bulkBody: (noun: string) =>
      `An image belongs to the ${noun} rather than to a revision, so this removes each of them from every ` +
      'revision that showed it, including the one readers have now. The bytes are gone for good; the only ' +
      'way back is to upload the files again.',
    /** The bulk delete's outcome, in up to three sentences: what went, what was gone, what still points. */
    bulkDeleted: (count: number, size: string) =>
      count === 0 ? 'Nothing was deleted.' : `${count === 1 ? '1 image was' : `${count} images were`} deleted (${size}).`,
    bulkAlreadyGone: (count: number) =>
      `${count === 1 ? '1 was' : `${count} were`} already gone - someone else may have deleted ` +
      `${count === 1 ? 'it' : 'them'}.`,
    bulkDangling: (count: number) =>
      `The published text still references ${count === 1 ? 'one of them once' : `them ${count} times`}, ` +
      'so readers will see placeholders until you remove the references and save.',
  },

  /** The Attachments card and the store-off notice it shares with the library page (ArticleAttachmentsPanel.tsx). */
  attachments: {
    heading: 'Attachments',
    noneListed: 'none listed',
    count: (count: number, max: number) => `${count} of ${max}`,
    addFromLibrary: 'Add from library',
    addFromLibraryHint: 'List a file the library already has',
    full: 'This article already lists as many attachments as it may.',
    uploadNew: 'Upload new',
    uploadNewHint: 'Upload a file to the library and list it here',
    chooseFiles: 'Choose files to attach',
    uploaded: (fileName: string) => `${fileName} was uploaded and listed on this article.`,
    /** `linked`: the text still links the file. */
    unlisted: (fileName: string, linked: boolean) =>
      `${fileName} is no longer listed on this article. It is still in the library` +
      (linked ? ', and the link to it in the text still works.' : '.'),
    listed: (fileName: string) => `${fileName} is now listed on this article.`,
    empty:
      'Nothing is listed. Files listed here are offered for download under the article; a file can also be ' +
      'linked from the text without being listed.',
    /** After the type and size, behind a "·". */
    alsoListedBy: (others: number) => `also listed by ${others} other article${others === 1 ? '' : 's'}`,
    linkedInText: 'linked in the text',
    download: 'Download',
    insertLink: 'Insert link',
    insertLinkHint: 'Insert a link to this file at the caret',
    remove: 'Remove',
    removeHint: "Take it off this article's list; the library keeps the file",
    noStore: 'This deployment has no attachment store.',
    /** {setting} is the setting's name, as code. */
    noStoreBody:
      'Files cannot be uploaded or downloaded until {setting} is set on the website. What is already listed is shown.',
  },

  /** "Add a file from the library" (ArticleAttachmentsPanel.tsx). */
  attachmentLibrary: {
    title: 'Add a file from the library',
    sharedHint: 'The file is shared, not copied: replacing it in the library replaces it here too.',
    findLabel: 'Find a file',
    findPlaceholder: 'File name…',
    loading: 'Loading the library…',
    allListed: 'Every file in the library is already listed here.',
    noMatch: 'No file in the library matches.',
    /** After the type and size, behind a "·". */
    listedBy: (count: number) => `listed by ${count === 1 ? '1 article' : `${count} articles`}`,
    add: 'Add',
    addFile: (fileName: string) => `Add ${fileName}`,
  },

  /** The preview's notices about the links in the text (Markdown.tsx, MarkdownPreview). */
  preview: {
    unresolved: (count: number) => `${count} article link${count === 1 ? '' : 's'} could not be resolved`,
    /** Why a token did not become a link, after the token and a dash. */
    linkFailure: {
      unwritten: 'that article has not been written yet, so there is nothing to link to.',
      notPublished: 'that article exists but is not published, so readers cannot follow the link.',
      notFound: 'no article has that id, or that product and slug.',
      other: 'the reference could not be resolved.',
    },
    unresolvedBody: 'Readers will see the link text as plain words rather than a link.',
    notLive: (count: number) => `${count} link${count === 1 ? '' : 's'} point at articles readers cannot see yet`,
    notLiveBody:
      'They resolve here because you are an administrator. For a reader they will render as plain text until ' +
      'the target article is published.',
    missingAttachments: (count: number) =>
      `${count} attachment link${count === 1 ? '' : 's'} could not be resolved`,
    noSuchAttachment: 'no attachment in the library has that id.',
    notAnAttachmentId: 'the target is not an attachment id.',
    missingAttachmentsBody: 'Readers will see the link text as plain words, or nothing where there is none.',
  },
};
